import React from 'react'
import moment from 'moment'
import { Box, Heading, Text } from '@chakra-ui/core'
import { InView } from 'react-intersection-observer'
import {
  Enum_Notification_Type,
  Maybe,
  NotificationEntity,
  useMarkNotificationAsReadMutation,
  useMarkNotificationAsReceivedMutation,
  UsersPermissionsUserEntity
} from '../../../generated/graphql'
import { NotificationCard } from '../../../components'
import { useAuthContext } from '../../../context/AuthProvider'
import { isNotificationRead } from '../utils'
import { captureEvent } from '../../../lib/analytics'

type NotificationListProps = {
  groupedNotificationsArray: any[]
  loading?: boolean
  onClickNotification?: (notificationId: Maybe<string>) => void
  onRefetchUnread?: () => Promise<any>
}
const NotificationList: React.FC<NotificationListProps> = ({
  groupedNotificationsArray,
  loading,
  onClickNotification = () => {},
  onRefetchUnread
}) => {
  const [markAsRead] = useMarkNotificationAsReadMutation()
  const { user } = useAuthContext()

  const [markNotificationAsReceived] = useMarkNotificationAsReceivedMutation()
  return (
    <>
      <Heading as="h3" size="md" p={4} fontWeight="bold">
        Notifications
      </Heading>
      {loading ? (
        <Text>Loading...</Text>
      ) : groupedNotificationsArray?.length > 0 ? (
        groupedNotificationsArray?.map(([date, notifications]) => (
          <Box key={date}>
            <Text fontSize="xs" fontWeight="bold" color="gray.600" p={4}>
              {moment(date).format('MMMM Do YYYY')}
            </Text>
            {notifications?.map((notification: Maybe<NotificationEntity>) => {
              const isRead = isNotificationRead(
                notification as NotificationEntity,
                user as UsersPermissionsUserEntity
              )

              const notificationType = notification?.attributes?.type
              return (
                <InView
                  key={notification?.id}
                  threshold={0.9}
                  onChange={(inView) => {
                    if (inView && !isRead && notification?.id) {
                    }
                  }}
                >
                  {({ ref }) => (
                    <Box ref={ref}>
                      <NotificationCard
                        title={notification?.attributes?.title || 'No title'}
                        message={notification?.attributes?.message || 'No message'}
                        createdAt={moment(notification?.attributes?.createdAt).fromNow()}
                        isRead={!!isRead}
                        type={notification?.attributes?.type as Enum_Notification_Type}
                        thumbnailUrl={
                          (notification as any)?.attributes?.attachment?.data?.attributes?.url
                            ? ((notification as any).attributes.attachment.data.attributes
                                .url as string)
                            : undefined
                        }
                        onClick={async () => {
                          captureEvent('dde_notification_opened', {
                            notification_type: notificationType || 'UNKNOWN',
                            was_unread: !isRead
                          })
                          if (!isRead) {
                            switch (notificationType) {
                              case Enum_Notification_Type.General:
                                await markNotificationAsReceived({
                                  variables: {
                                    notificationId: notification?.id || ''
                                  }
                                })
                                break
                              case Enum_Notification_Type.MoreInfoRequested:
                                await markAsRead({
                                  variables: { id: notification?.id || '' }
                                })
                                break

                              default:
                                break
                            }
                          }
                          onClickNotification(notification?.id || '')
                        }}
                        rightSlot={
                          notification?.attributes?.type === Enum_Notification_Type.ReminderAlert &&
                          notification?.attributes?.nextScheduledNotificationDetails?.sendAt ? (
                            <Text fontSize="xs" color="gray.500">
                              {moment(
                                notification?.attributes?.nextScheduledNotificationDetails?.sendAt
                              ).format('lll')}
                            </Text>
                          ) : undefined
                        }
                      />
                    </Box>
                  )}
                </InView>
              )
            })}
          </Box>
        ))
      ) : (
        <Text p={4}>No notifications</Text>
      )}
    </>
  )
}

export default NotificationList
